/**
 * PATH: apps/quote/src/pages/import-quote/ImportInputPanel.tsx
 * FILE_ID: QUOTE-IMPORT-INPUT-PANEL-001
 * PURPOSE: Step 1 of quote import — device, QDrive, existing quotes, or paste.
 * WHY: File Parse uploads onto a job with allow_ai=false. Paste AI is separate.
 */
import { useRef, useState } from 'react'
import { Upload, ClipboardPaste, Sparkles, Loader2, FileText, HardDrive, Library } from 'lucide-react'
import { QDriveFilePicker } from '@quotech/quote-builder'
import { cn } from '@/lib/utils'
import { qdriveFilesApi } from '@/lib/api/qdrive-files-api'
import { ImportExistingQuotesPanel } from './ImportExistingQuotesPanel'

export type ImportInputMode = 'upload' | 'qdrive' | 'paste' | 'existing'

interface Props {
  mode: ImportInputMode
  setMode: (m: ImportInputMode) => void
  text: string
  setText: (t: string) => void
  file: File | null
  setFile: (f: File | null) => void
  onParse: () => void
  parsing: boolean
  parseError?: string | null
  onCreateJob?: () => void
  jobLabel?: string | null
  jobId?: string | null
  onImportExisting: (ids: string[]) => void
  importingExisting?: boolean
}

/** Spreadsheet-first, because that is what an external quote usually is. */
const ACCEPT = [
  '.xlsx', '.xlsm', '.xls', '.csv', '.tsv', '.tab', '.ods',
  '.pdf', '.docx', '.txt',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'text/tab-separated-values',
].join(',')

export function ImportInputPanel({
  mode, setMode, text, setText, file, setFile,   onParse, parsing, parseError, onCreateJob, jobLabel,
  jobId, onImportExisting, importingExisting,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [qdriveBusy, setQdriveBusy] = useState(false)
  const [qdriveError, setQdriveError] = useState<string | null>(null)
  const canParse = !parsing && !qdriveBusy && (
    mode === 'paste' ? text.trim().length > 0 : Boolean(file && jobId)
  )

  const chooseFromQDrive = async (picked: { id: string; filename: string; content_type?: string | null }) => {
    setQdriveBusy(true)
    setQdriveError(null)
    try {
      setFile(await qdriveFilesApi.download(picked))
    } catch (err) {
      setQdriveError(err instanceof Error ? err.message : 'Could not load that QDrive file.')
      throw err
    } finally {
      setQdriveBusy(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {jobLabel && (
        <div className="flex items-center gap-2 text-sm rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2 text-blue-800 dark:text-blue-200">
          <FileText className="h-4 w-4" />
          Importing onto job <span className="font-semibold">{jobLabel}</span> — costs will track in this job's BI.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <TabButton active={mode === 'upload'} onClick={() => setMode('upload')} icon={<Upload className="h-4 w-4" />} label="Upload file" />
        <TabButton active={mode === 'qdrive'} onClick={() => setMode('qdrive')} icon={<HardDrive className="h-4 w-4" />} label="From QDrive" />
        <TabButton active={mode === 'existing'} onClick={() => setMode('existing')} icon={<Library className="h-4 w-4" />} label="Existing quotes" />
        <TabButton active={mode === 'paste'} onClick={() => setMode('paste')} icon={<ClipboardPaste className="h-4 w-4" />} label="Paste text" />
      </div>

      {mode === 'paste' && (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste the quote here — line items, prices, sections. AI will map it into your format."
          className="w-full h-64 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
      )}

      {mode === 'upload' && (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f) }}
          className="cursor-pointer rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 hover:border-blue-400 p-10 text-center"
        >
          <input ref={inputRef} type="file" accept={ACCEPT} className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <Upload className="h-8 w-8 mx-auto text-gray-400" />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {file ? <span className="font-medium">{file.name}</span> : 'Drop an Excel, CSV, TSV or PDF here, or click to browse'}
          </p>
        </div>
      )}

      {mode === 'qdrive' && (
        <div className="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 p-10 text-center">
          <HardDrive className="h-8 w-8 mx-auto text-gray-400" />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {file ? <span className="font-medium">{file.name}</span> : 'Pick a quote already filed in QDrive.'}
          </p>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            disabled={qdriveBusy}
            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-60"
          >
            {qdriveBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <HardDrive className="h-4 w-4" />}
            {file ? 'Choose a different file' : 'Browse QDrive'}
          </button>
          {qdriveError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{qdriveError}</p>}
        </div>
      )}

      {mode === 'existing' && (
        <ImportExistingQuotesPanel
          jobId={jobId}
          importing={!!importingExisting}
          onImport={onImportExisting}
        />
      )}

      {mode !== 'existing' && (
        <div className="flex justify-end">
          <button
            onClick={onParse}
            disabled={!canParse}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors',
              canParse ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed',
            )}
          >
            {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {parsing ? 'Parsing…' : 'Parse'}
          </button>
        </div>
      )}
      {parseError ? (
        <p className="text-sm text-red-600 dark:text-red-400">{parseError}</p>
      ) : null}
      {!jobId && mode !== 'existing' && mode !== 'paste' ? (
        <p className="text-sm text-amber-800 dark:text-amber-200">
          Create a job first, then parse the Excel onto it.
          {onCreateJob ? (
            <button type="button" onClick={onCreateJob} className="ml-2 underline font-medium">
              New job
            </button>
          ) : (
            <a href="https://dashboard.quotech.ai/jobs" className="ml-2 underline font-medium">
              Open Jobs
            </a>
          )}
        </p>
      ) : null}

      <QDriveFilePicker
        open={pickerOpen}
        title="Choose a quote from QDrive"
        description="Search QDrive and import the quote without downloading it first."
        onClose={() => setPickerOpen(false)}
        loadFiles={qdriveFilesApi.list}
        onSelect={chooseFromQDrive}
      />
    </div>
  )
}

function TabButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
        active ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700',
      )}
    >
      {icon}{label}
    </button>
  )
}
